import { useCallback, useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ClientEnrollmentStatus from "@/components/app/ClientEnrollmentStatus";
import ClientEnrollmentDetail from "@/components/app/ClientEnrollmentDetail";
import ProfiledCell from "@/components/app/ProfiledCell";
import { toast } from "sonner";
import { RotateCcw, Download, Ban, Eye, Trash2 } from "lucide-react";

const emptyF = { client_id: "all", course_id: "all", status: "all", profiled: "all", date_from: "", date_to: "" };
const params = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));

function Filters({ f, setF, clients, courses, withStatus }) {
  return (
    <Card className="border-slate-200"><CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
      <div><Label className="text-xs">Cliente</Label>
        <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
          <SelectTrigger data-testid="rep-filter-client"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i clienti</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs">Corso</Label>
        <Select value={f.course_id} onValueChange={(v) => setF({ ...f, course_id: v })}>
          <SelectTrigger data-testid="rep-filter-course"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i corsi</SelectItem>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {withStatus && <div><Label className="text-xs">Stato</Label>
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
          <SelectTrigger data-testid="rep-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="confermata">Confermata</SelectItem><SelectItem value="annullata">Annullata</SelectItem></SelectContent>
        </Select>
      </div>}
      {withStatus && <div><Label className="text-xs">Lavorazione</Label>
        <Select value={f.profiled} onValueChange={(v) => setF({ ...f, profiled: v })}>
          <SelectTrigger data-testid="rep-filter-profiled"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="no">Da profilare</SelectItem><SelectItem value="si">Profilati</SelectItem></SelectContent>
        </Select>
      </div>}
      <div><Label className="text-xs">Data inizio</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="rep-filter-from" /></div>
      <div><Label className="text-xs">Data fine</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="rep-filter-to" /></div>
      <Button variant="outline" onClick={() => setF(emptyF)} data-testid="rep-filter-reset"><RotateCcw className="w-4 h-4 mr-1" /> Azzera</Button>
    </CardContent></Card>
  );
}

function ExportButtons({ path, f, prefix }) {
  const href = (fmt) => `${API}${path}?${new URLSearchParams({ ...params(f), format: fmt }).toString()}`;
  return (
    <div className="flex gap-2 flex-wrap">
      {[["xlsx", "Excel"], ["csv", "CSV"], ["pdf", "PDF"]].map(([fmt, l]) => (
        <Button key={fmt} asChild variant="outline" size="sm" data-testid={`${prefix}-export-${fmt}`}>
          <a href={href(fmt)} target="_blank" rel="noopener noreferrer"><Download className="w-4 h-4 mr-1" /> {l}</a>
        </Button>
      ))}
    </div>
  );
}

function EnrollmentsTab({ f }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/client-enrollments", { params: params(f) }).then((r) => setRows(r.data)), [f]);
  useEffect(() => { load(); }, [load]);
  const cancel = async (e) => {
    const msg = e.slot_used ? `Annullare l'iscrizione ${e.enrollment_number}? Lo slot verrà restituito al cliente.` : `Annullare l'iscrizione ${e.enrollment_number}?`;
    if (!window.confirm(msg)) return;
    try { const { data } = await api.post(`/client-enrollments/${e.id}/cancel`, { notes: "" }); toast.success(data.slot_restored ? "Iscrizione annullata · slot restituito" : "Iscrizione annullata"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleProfiled = async (e) => {
    try { await api.post(`/client-enrollments/${e.id}/profiled`, { value: !e.profiled }); toast.success(e.profiled ? "Profilazione rimossa" : `${e.first_name} ${e.last_name} segnato come Profilato`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [detail, setDetail] = useState(null);
  const del = async (e) => {
    const msg = `Eliminare definitivamente l'iscrizione di ${e.first_name} ${e.last_name}?${e.status === "confermata" && e.slot_used ? " Lo slot verrà restituito al cliente." : ""} L'azione sarà registrata nei log.`;
    if (!window.confirm(msg)) return;
    try { const { data } = await api.delete(`/client-enrollments/${e.id}`); toast.success(data.slot_restored ? "Iscritto eliminato · slot restituito" : "Iscritto eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <>
    <ClientEnrollmentDetail item={detail} onClose={() => setDetail(null)} />
    <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Data</TableHead><TableHead>Numero</TableHead><TableHead>Cliente</TableHead><TableHead>Corsista</TableHead>
          <TableHead>Corso</TableHead><TableHead>Prezzo</TableHead><TableHead>Modalità</TableHead><TableHead>Stato</TableHead><TableHead>Profilato</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((e) => (
            <TableRow key={e.id} data-testid={`admin-client-enr-${e.enrollment_number}`}>
              <TableCell className="text-xs">{dateIt(e.created_at)}</TableCell>
              <TableCell className="font-semibold">{e.enrollment_number}</TableCell>
              <TableCell>{e.client_name}</TableCell>
              <TableCell>{e.first_name} {e.last_name}<div className="text-xs text-slate-500">{e.fiscal_code}</div></TableCell>
              <TableCell>{e.course_title}{e.edition_label && <div className="text-xs text-slate-500">{e.edition_label}</div>}</TableCell>
              <TableCell>{eur(e.unit_price)}</TableCell>
              <TableCell className="text-xs">{e.billing_mode === "slot" ? "Slot" : "Mensile"}</TableCell>
              <TableCell><ClientEnrollmentStatus status={e.status} /></TableCell>
              <TableCell>
                <ProfiledCell item={e} onToggle={toggleProfiled} testKey={e.enrollment_number} disabled={e.status !== "confermata"} />
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <Button variant="ghost" size="icon" onClick={() => setDetail(e)} aria-label="Scheda iscritto" data-testid={`view-client-enr-${e.enrollment_number}`}><Eye className="w-4 h-4 text-slate-600" /></Button>
                {e.status === "confermata" && <Button variant="ghost" size="icon" title="Annulla iscrizione" onClick={() => cancel(e)} data-testid={`cancel-client-enr-${e.enrollment_number}`}><Ban className="w-4 h-4 text-amber-600" /></Button>}
                <Button variant="ghost" size="icon" onClick={() => del(e)} aria-label="Elimina" data-testid={`delete-client-enr-${e.enrollment_number}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-10">Nessuna iscrizione.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </CardContent></Card>
    </>
  );
}

function BillingTab({ f }) {
  const [d, setD] = useState({ rows: [], totals: {} });
  useEffect(() => { api.get("/client-billing", { params: params(f) }).then((r) => setD(r.data)); }, [f]);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-500">Iscrizioni confermate dei clienti in Fatturazione mensile.</div>
        <ExportButtons path="/client-billing/export" f={f} prefix="billing" />
      </div>
      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Cliente</TableHead><TableHead>Corso</TableHead><TableHead>N. iscrizioni</TableHead><TableHead>Costo unitario</TableHead>
            <TableHead>Imponibile</TableHead><TableHead>IVA</TableHead><TableHead>Totale da fatturare</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={i} data-testid={`billing-row-${i}`}>
                <TableCell className="font-semibold">{r.client_name}</TableCell><TableCell>{r.course_title}</TableCell>
                <TableCell>{r.count}</TableCell><TableCell>{eur(r.unit_price)}</TableCell><TableCell>{eur(r.net_total)}</TableCell>
                <TableCell>{r.vat_rate}% · {eur(r.vat_total)}</TableCell><TableCell className="font-bold">{eur(r.total)}</TableCell>
              </TableRow>
            ))}
            {d.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">Nessun importo da fatturare nel periodo.</TableCell></TableRow>}
            {d.rows.length > 0 && (
              <TableRow className="bg-slate-50 font-bold" data-testid="billing-totals">
                <TableCell colSpan={2}>Totale</TableCell><TableCell>{d.totals.count}</TableCell><TableCell></TableCell>
                <TableCell>{eur(d.totals.net_total)}</TableCell><TableCell>{eur(d.totals.vat_total)}</TableCell><TableCell data-testid="billing-grand-total">{eur(d.totals.total)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

function ReportTab({ f }) {
  const [d, setD] = useState({ rows: [], totals: {} });
  useEffect(() => { api.get("/client-report", { params: params(f) }).then((r) => setD(r.data)); }, [f]);
  const t = d.totals;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Iscrizioni", t.enrollments, "rep-total-enrollments"], ["Importi maturati", eur(t.amount_accrued || 0), "rep-total-accrued"], ["Slot utilizzati", t.slots_used, "rep-total-slots"], ["Da fatturare (IVA incl.)", eur(t.to_bill_total || 0), "rep-total-bill"]].map(([l, v, id]) => (
          <Card key={id} className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest font-bold text-amber-600">{l}</div><div className="font-display font-extrabold text-2xl mt-1" data-testid={id}>{v ?? 0}</div></CardContent></Card>
        ))}
      </div>
      <div className="flex justify-end"><ExportButtons path="/client-report/export" f={f} prefix="report" /></div>
      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Cliente</TableHead><TableHead>Modalità</TableHead><TableHead>N. iscrizioni</TableHead><TableHead>Importi maturati</TableHead>
            <TableHead>Slot utilizzati</TableHead><TableHead>Slot residui</TableHead><TableHead>Totale da fatturare</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r) => (
              <TableRow key={r.client_id} data-testid={`report-row-${r.client_id}`}>
                <TableCell className="font-semibold">{r.client_name}</TableCell>
                <TableCell className="text-xs">{r.billing_mode === "slot" ? "Slot prepagati" : "Fatturazione mensile"}</TableCell>
                <TableCell>{r.enrollments}</TableCell><TableCell>{eur(r.amount_accrued)}</TableCell>
                <TableCell>{r.slots_used}</TableCell><TableCell>{r.slots_remaining ?? "—"}</TableCell>
                <TableCell className="font-bold">{r.billing_mode === "mensile" ? <>{eur(r.to_bill_total)} <span className="text-xs font-normal text-slate-500">({eur(r.to_bill_net)} + IVA)</span></> : "Prepagato"}</TableCell>
              </TableRow>
            ))}
            {d.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">Nessun dato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

export default function AdminClientReports({ mode = "reports" }) {
  const isEnr = mode === "enrollments";
  const [f, setF] = useState(emptyF);
  const [tab, setTab] = useState("billing");
  const [clients, setClients] = useState([]);
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/courses").then((r) => setCourses(r.data));
    if (isEnr) api.post("/client-enrollments/mark-all-seen").catch(() => {}).finally(() => window.dispatchEvent(new Event("badges:refresh")));
  }, [isEnr]);
  return (
    <div className="space-y-6" data-testid={isEnr ? "admin-client-enrollments" : "admin-client-reports"}>
      <div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">{isEnr ? "Iscrizioni clienti" : "Report clienti"}</h1>
        <p className="text-slate-500 mt-1">{isEnr ? "Iscrizioni effettuate dai clienti: annullamento e profilazione." : "Riepilogo fatturazione mensile e report con export."}</p>
      </div>
      <Filters f={f} setF={setF} clients={clients} courses={courses} withStatus={isEnr} />
      {isEnr ? <EnrollmentsTab f={f} /> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="billing" data-testid="tab-client-billing">Riepilogo Fatturazione Cliente</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-client-report">Report</TabsTrigger>
          </TabsList>
          <TabsContent value="billing"><BillingTab f={f} /></TabsContent>
          <TabsContent value="report"><ReportTab f={f} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
